import React, {
    createContext,
    useEffect,
    useMemo,
    useReducer,
    type ReactNode,
} from 'react';
import AnimationUtils from '@spothero/utils/animation';

interface AutoScrollItem {
    selector: string;
    duration?: number;
    onComplete?: () => void;
    parentContainer?: string;
}

interface AutoScrollContextValue {
    removeFromQueue: (selector?: string) => void;
    addToScrollQueue: (item: AutoScrollItem) => void;
    scrollQueue: AutoScrollItem[];
}

const ENQUEUE = 'ENQUEUE';
const DEQUEUE = 'DEQUEUE';
const initialState: AutoScrollItem[] = [];

type Action =
    | {type: typeof ENQUEUE; payload: AutoScrollItem}
    | {type: typeof DEQUEUE; payload?: string};

const reducer = (state: AutoScrollItem[] = initialState, action: Action) => {
    switch (action.type) {
        case ENQUEUE:
            return state.concat(action.payload);
        case DEQUEUE:
            // either remove all items in array that match payload, or the first item
            return action.payload
                ? state.filter((item) => item.selector !== action.payload)
                : state.slice(1);
        default:
            return state;
    }
};

/**
 * Manages the scroll state.  To be used with the {@link AutoScrollProvider} component only.
 * @private
 *
 *
 * @memberof module:hooks/autoScroll
 */
const useAutoScrollState = () => {
    const [queue, dispatch] = useReducer(reducer, initialState);
    const removeFromQueue = useMemo(
        () => (selector?: string | undefined) =>
            dispatch({type: DEQUEUE, payload: selector}),
        []
    );
    const addToScrollQueue = useMemo(
        () => (item: AutoScrollItem) =>
            dispatch({type: ENQUEUE, payload: item}),
        []
    );

    // this is the maintainer of the queue
    useEffect(() => {
        if (queue.length) {
            // TODO update useAutoScroll for component ref support
            const {selector, duration, onComplete, parentContainer} = queue[0];
            const element = document.querySelector<HTMLElement>(selector);

            if (!element) {
                return;
            }

            AnimationUtils.scrollTo(
                parentContainer || 'html',
                element.getBoundingClientRect().y,
                duration || 1000,
                'y',
                () => {
                    if (onComplete) {
                        onComplete();
                    }
                    removeFromQueue();
                }
            );
        }
    }, [queue, removeFromQueue]);

    return {
        removeFromQueue,
        addToScrollQueue,
        scrollQueue: queue,
    };
};

/**
 * Context for the AutoScrollProvider
 *
 * @memberof module:hooks/autoScroll
 */
export const AutoScrollContext = createContext<AutoScrollContextValue | null>(
    null
);

/**
 * Provider for the AutoScrollContext.  To only be used within App.jsx since having more than one of these providers could cause scroll animation bubbling issues.
 * @function AutoScrollProvider
 *
 * @memberof module:hooks/autoScroll
 */
export const AutoScrollProvider = ({children}: {children: ReactNode}) => {
    const {removeFromQueue, addToScrollQueue, scrollQueue} =
        useAutoScrollState();

    return (
        <AutoScrollContext.Provider
            value={{
                removeFromQueue,
                addToScrollQueue,
                scrollQueue,
            }}
        >
            {children}
        </AutoScrollContext.Provider>
    );
};
